// Minisite link + WhatsApp click-to-chat share flows for the Matches tab.
// PORTED from ListingMatchContactListingsBottomOverlay (client-side wa.me flow — there
// is deliberately NO server-side "send listing batch to customer" path today), with the
// share text properly URL-encoded (the legacy overlay embedded the raw link).
import { PATH_PUBLIC } from "routes/paths";
import { getUserFullName } from "utils/Haim/utils";
import type { RealEstateContact } from "../../../../../../@types/sharedSchema";

export const buildMinisiteUrl = (favoritesDocId: string): string =>
  `${window.location.origin}${PATH_PUBLIC.myListings}/${favoritesDocId}`;

/** Deep link straight to one listing inside the contact's public minisite. */
export const buildMinisiteListingUrl = (favoritesDocId: string, listingId: string): string =>
  `${buildMinisiteUrl(favoritesDocId)}/${listingId}`;

/**
 * WhatsApp click-to-chat URL. The phone (contact.id, international +972…) is reduced to
 * digits — api.whatsapp.com rejects the leading "+ " in some clients. Without a phone
 * the share sheet lets the realtor pick the recipient (legacy behavior).
 */
export function buildWhatsAppShareUrl(phone: string | undefined, text: string): string {
  const digits = (phone ?? "").replace(/\D/g, "");
  const encoded = encodeURIComponent(text);
  return digits
    ? `https://api.whatsapp.com/send?phone=${digits}&text=${encoded}`
    : `https://api.whatsapp.com/send?text=${encoded}`;
}

/**
 * FIRST name only — what every WhatsApp we send the contact greets them with.
 *
 * `getUserFirstNameFallBackToPhoneNumber` (the repo's existing helper) returns
 * `callerFirstName` VERBATIM, and that field very often holds the whole display name
 * ("דנה כהן" — WhatsApp profile names and phone-book imports land there unsplit), so
 * greeting with it reads "היי דנה כהן". We take the first whitespace-delimited token of
 * the contact's full name instead.
 *
 * Hebrew-safe: the split is on whitespace only (no letter classes, no direction
 * assumptions) and RTL text is stored in logical order, so the first token IS the first
 * name. `\s` also covers the NBSP that pasted contact names often carry.
 *
 * Deliberately NOT falling back to the phone number the way the shared helper does: a
 * national format can itself contain spaces ("(212) 555-1234"), and half a phone number
 * is not a greeting. A contact with no name at all yields "" and both builders below
 * then drop the name from the greeting entirely ("היי, ריכזתי עבורך...").
 */
export function getContactFirstName(contact: RealEstateContact | undefined): string {
  const fullName = getUserFullName(contact).trim();
  if (!fullName) return "";
  // Trimmed + non-empty ⇒ there is always a first token; `|| fullName` is belt-and-braces.
  return fullName.split(/\s+/)[0] || fullName;
}

export function buildMinisiteShareMessage(contact: RealEstateContact, url: string): string {
  const name = getContactFirstName(contact);
  return `היי${name ? ` ${name}` : ""}, ריכזתי עבורך נכסים שיכולים להתאים לך 🏠\n${url}`;
}

/**
 * The ONE per-card WhatsApp follow-up (the bell/reminder action was folded into this
 * — product decision): "הי <שם>, מה עם הנכס הזה?" + the listing's address line and
 * its minisite deep link.
 */
export function buildListingFollowUpMessage(
  contact: RealEstateContact,
  addressLine: string,
  listingUrl: string
): string {
  const name = getContactFirstName(contact);
  return `הי${name ? ` ${name}` : ""}, מה עם הנכס הזה? 🏠\n${addressLine}\n${listingUrl}`;
}

export function openWhatsAppShare(contact: RealEstateContact, text: string): void {
  window.open(buildWhatsAppShareUrl(contact.id, text), "_blank", "noopener,noreferrer");
}
